import Dexie, { Table } from "dexie"

export interface RecordingCaptureMetadata {
  ended_at?: string
  duration_ms?: number
  signal_detected?: boolean
  monitored_ms?: number
  silent_ms?: number
  unverified_ms?: number
  issues?: string[]
  lost_sequences?: number[]
}

export interface RecordingChunk {
  id?: number
  sessionGuid: string
  sequence: number
  data: ArrayBuffer | null
  mimeType: string
  uploadedAt: Date | null
  createdAt: Date
  s3Key: string | null
}

export interface RecordingChunkWithBlob extends Omit<RecordingChunk, "data" | "mimeType"> {
  blob: Blob
}

export interface RecordingSession {
  sessionGuid: string
  meetingId: string
  meetingTitle: string | null
  startedAt: Date
  totalChunks: number
  uploadedChunks: number
  status: "recording" | "uploading" | "completed" | "failed"
  mimeType: string
  sessionId: string | null
  lastActivityAt: Date | null
  finalizedAt: Date | null
  dismissedAt: Date | null
  capture?: RecordingCaptureMetadata
}

class RecordingDatabase extends Dexie {
  chunks!: Table<RecordingChunk>
  sessions!: Table<RecordingSession>

  constructor() {
    super("SlantRecordingSessions")

    this.version(1).stores({
      chunks: "++id, [sessionGuid+sequence], [sessionGuid+uploadedAt], sessionGuid",
      sessions: "sessionGuid, status, meetingId",
    })
  }
}

export const recordingDb = new RecordingDatabase()

export async function saveChunk(sessionGuid: string, sequence: number, blob: Blob): Promise<number> {
  if (!sessionGuid) {
    throw new Error(`saveChunk called with invalid sessionGuid: ${sessionGuid}`)
  }
  const data = await blob.arrayBuffer()
  const id = await recordingDb.chunks.add({
    sessionGuid,
    sequence,
    data,
    mimeType: blob.type,
    uploadedAt: null,
    createdAt: new Date(),
    s3Key: null,
  })
  return id as number
}

export async function markChunkUploaded(chunkId: number, s3Key: string): Promise<void> {
  const updated = await recordingDb.chunks.update(chunkId, {
    uploadedAt: new Date(),
    s3Key,
    data: null,
  })

  if (updated !== 1) {
    throw new Error(`Recording chunk ${chunkId} no longer exists in local storage`)
  }
}

function chunkToBlob(chunk: RecordingChunk): RecordingChunkWithBlob {
  const { data, mimeType, ...rest } = chunk
  if (!data) {
    throw new Error(`Pending recording chunk ${chunk.id} has no local audio data`)
  }
  return { ...rest, blob: new Blob([data], { type: mimeType }) }
}

export async function getPendingChunks(sessionGuid: string): Promise<RecordingChunkWithBlob[]> {
  const chunks = await recordingDb.chunks
    .where({ sessionGuid })
    .filter((chunk) => chunk.uploadedAt === null)
    .sortBy("sequence")
  return chunks.map(chunkToBlob)
}

export async function getChunkCount(sessionGuid: string): Promise<{ total: number; uploaded: number }> {
  const all = await recordingDb.chunks.where({ sessionGuid }).count()
  const uploaded = await recordingDb.chunks
    .where({ sessionGuid })
    .filter((chunk) => chunk.uploadedAt !== null)
    .count()
  return { total: all, uploaded }
}

export async function createSession(
  sessionGuid: string,
  meetingId: string,
  mimeType: string,
  sessionId: string | null,
  meetingTitle: string | null
): Promise<void> {
  if (!sessionGuid) {
    throw new Error(`createSession called with invalid sessionGuid: ${sessionGuid}`)
  }
  await recordingDb.sessions.put({
    sessionGuid,
    meetingId,
    meetingTitle,
    startedAt: new Date(),
    totalChunks: 0,
    uploadedChunks: 0,
    status: "recording",
    mimeType,
    sessionId,
    lastActivityAt: new Date(),
    finalizedAt: null,
    dismissedAt: null,
  })
}

export async function getSessionByMeetingId(meetingId: string): Promise<RecordingSession | undefined> {
  return recordingDb.sessions.where("meetingId").equals(meetingId).first()
}

export async function updateSession(sessionGuid: string, updates: Partial<RecordingSession>): Promise<void> {
  if (!sessionGuid) {
    throw new Error(`updateSession called with invalid sessionGuid: ${sessionGuid}`)
  }
  const updated = await recordingDb.sessions.update(sessionGuid, updates)
  if (updated !== 1) throw new Error("The local recording session is no longer available")
}

export async function getSession(sessionGuid: string): Promise<RecordingSession | undefined> {
  return recordingDb.sessions.get(sessionGuid)
}

export async function getIncompleteSessions(): Promise<RecordingSession[]> {
  return recordingDb.sessions.where("status").anyOf(["recording", "uploading", "failed"]).toArray()
}

export async function getUndismissedSession(): Promise<RecordingSession | undefined> {
  const sessions = await recordingDb.sessions
    .where("status")
    .anyOf(["recording", "uploading", "failed"])
    .filter((session) => !session.dismissedAt)
    .toArray()
  return sessions.sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())[0]
}

export async function dismissSession(sessionGuid: string): Promise<void> {
  await recordingDb.sessions.update(sessionGuid, { dismissedAt: new Date() })
}

export async function reclaimUploadedChunkData(): Promise<{ chunks: number; bytes: number }> {
  let bytes = 0

  const chunks = await recordingDb.chunks
    .filter((chunk) => chunk.uploadedAt !== null && chunk.data !== null)
    .modify((chunk) => {
      bytes += chunk.data?.byteLength ?? 0
      chunk.data = null
    })

  return { chunks, bytes }
}

export async function deleteSessionAndChunks(sessionGuid: string): Promise<void> {
  await recordingDb.transaction("rw", recordingDb.chunks, recordingDb.sessions, async () => {
    await recordingDb.chunks.where({ sessionGuid }).delete()
    await recordingDb.sessions.delete(sessionGuid)
  })
}

export async function cleanupOldRecordings(): Promise<number> {
  const now = new Date()
  const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

  // Old frontend versions retained every uploaded Blob. Release only data that is already safe in S3;
  // a legacy completed session can still contain a final chunk that lost the old finalize race.
  try {
    await reclaimUploadedChunkData()
  } catch {
    // Cleanup is best effort. Recording startup retries reclamation and reports failures to Sentry.
  }

  const oldSessions = await recordingDb.sessions
    .filter((session) => {
      const completedAndDismissedLongAgo =
        session.status === "completed" && session.dismissedAt !== null && session.dismissedAt < threeDaysAgo
      const veryOld = session.status === "completed" && session.startedAt < sevenDaysAgo
      return completedAndDismissedLongAgo || veryOld
    })
    .toArray()

  let deletedCount = 0

  for (const session of oldSessions) {
    await deleteSessionAndChunks(session.sessionGuid)
    deletedCount++
  }

  return deletedCount
}
